import {toLatinDigits} from '../../../../_metronic/helpers/components/datepicker/persianDateValue'

// The username and password rules of «امنیت و ورود», as the server applies them
// (MegaPayBackend Services/Profile/ProfileSecurityRules). The panel previews them; the server decides.

export const USERNAME_MIN = 5
export const USERNAME_MAX = 25
export const PASSWORD_MIN = 8
export const PASSWORD_MAX = 64

export const usernameProblem = (candidate: string, current?: string | null): string => {
  const value = candidate.trim()
  if (!value) return 'نام کاربری را وارد کنید.'
  if (value === (current || '').trim()) return ''
  if (value.length < USERNAME_MIN) return 'نام کاربری باید دست‌کم ۵ نویسه باشد.'
  if (value.length > USERNAME_MAX) return 'نام کاربری حداکثر ۲۵ نویسه است.'
  if (!/^[A-Za-z0-9.]+$/.test(value)) return 'در نام کاربری فقط حروف انگلیسی، رقم و نقطه مجاز است.'
  if (value.startsWith('.') || value.endsWith('.') || value.includes('..'))
    return 'نقطه نباید در ابتدا یا انتهای نام کاربری بیاید یا دو بار پشت سر هم تکرار شود.'
  return ''
}

export type PasswordCheck = {
  key: 'length' | 'upper' | 'lower' | 'digit' | 'space' | 'personal'
  label: string
  passed: boolean
}

// Sign-in turns Persian digits into Latin ones before comparing; the checks read the same value.
const normalize = (password: string) => toLatinDigits(password.trim())

export const passwordChecks = (
  password: string,
  username?: string | null,
  mobile?: string | null
): PasswordCheck[] => {
  const value = normalize(password)
  const name = (username || '').trim().toLowerCase()
  const mobileDigits = String(mobile || '').replace(/\D/g, '')
  const lastTen = mobileDigits.length >= 10 ? mobileDigits.slice(-10) : ''
  const personal =
    value.length > 0 &&
    !(name.length >= 3 && value.toLowerCase().includes(name)) &&
    !(lastTen && value.replace(/\D/g, '').includes(lastTen))

  return [
    {
      key: 'length',
      label: 'دست‌کم ۸ نویسه',
      passed: value.length >= PASSWORD_MIN && value.length <= PASSWORD_MAX,
    },
    {key: 'upper', label: 'یک حرف بزرگ انگلیسی', passed: /[A-Z]/.test(value)},
    {key: 'lower', label: 'یک حرف کوچک انگلیسی', passed: /[a-z]/.test(value)},
    {key: 'digit', label: 'یک رقم', passed: /[0-9]/.test(value)},
    {key: 'space', label: 'بدون فاصله', passed: value.length > 0 && !/\s/.test(value)},
    {key: 'personal', label: 'بدون نام کاربری و شماره موبایل', passed: personal},
  ]
}

export const passwordAccepted = (
  password: string,
  username?: string | null,
  mobile?: string | null
) => passwordChecks(password, username, mobile).every((check) => check.passed)

// 0 (nothing typed) to 4 (strong): length and variety beyond the minimum rules.
export const passwordStrength = (password: string): 0 | 1 | 2 | 3 | 4 => {
  const value = normalize(password)
  if (!value) return 0
  const classes = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) =>
    pattern.test(value)
  ).length
  let score = 0
  if (value.length >= PASSWORD_MIN) score++
  if (value.length >= 12) score++
  if (classes >= 3) score++
  if (classes === 4 || value.length >= 16) score++
  return Math.max(1, Math.min(4, score)) as 1 | 2 | 3 | 4
}

export const STRENGTH_LABELS = ['', 'ضعیف', 'متوسط', 'خوب', 'قوی']

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const LOWER = 'abcdefghijkmnopqrstuvwxyz'
const DIGITS = '23456789'
const SYMBOLS = '!@#$%*?-'

// A strong suggestion from the browser's cryptographic generator: every class at least once and
// no look-alike letters (O/0, l/1, I).
export const generatePassword = (
  length = 14,
  random: (size: number) => Uint32Array = randomValues
) => {
  const pools = [UPPER, LOWER, DIGITS, SYMBOLS]
  const all = pools.join('')
  const values = random(length * 2)
  const chars: string[] = pools.map((pool, index) => pool[values[index] % pool.length])
  for (let index = chars.length; index < length; index++)
    chars.push(all[values[index] % all.length])
  // Shuffle so the guaranteed classes do not always lead.
  for (let index = chars.length - 1; index > 0; index--) {
    const swap = values[length + index] % (index + 1)
    ;[chars[index], chars[swap]] = [chars[swap], chars[index]]
  }
  return chars.join('')
}

function randomValues(size: number) {
  const values = new Uint32Array(size)
  window.crypto.getRandomValues(values)
  return values
}
